import type Lenis from 'lenis'
import type { FC, ReactNode } from 'react'

import { createContext, useContext, useEffect, useState } from 'react'
import { gsap, ScrollTrigger } from '@/lib/motion'

interface SmoothScrollProps {
    children: ReactNode
}

const LenisContext = createContext<Lenis | null>(null)

// Lenis instance for programmatic scrolling (anchor links, page changes).
// It is null on the server and until the library has loaded on the client.
export const useLenis = (): Lenis | null => useContext(LenisContext)

const SmoothScroll: FC<SmoothScrollProps> = ({ children }): ReactNode => {
    const [lenis, setLenis] = useState<Lenis | null>(null)

    useEffect(() => {
        let instance: Lenis | null = null
        let cancelled = false

        const tick = (time: number): void => {
            instance?.raf(time * 1000)
        }

        import('lenis').then(({ default: LenisClass }) => {
            if (cancelled) return

            // Lenis honours prefers-reduced-motion on its own (smoothing off).
            instance = new LenisClass({
                lerp: 0.11,
                smoothWheel: true,
                autoRaf: false,
                anchors: false
            })

            instance.on('scroll', ScrollTrigger.update)
            gsap.ticker.add(tick)
            gsap.ticker.lagSmoothing(0)
            setLenis(instance)
        })

        return () => {
            cancelled = true
            gsap.ticker.remove(tick)
            instance?.destroy()
        }
    }, [])

    return (
        <LenisContext.Provider value={lenis}>
            {children}
        </LenisContext.Provider>
    )
}

export default SmoothScroll
